import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { RefreshCw, Download, Trash2, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusConfig = {
  success: { label: 'Succès', color: 'bg-emerald-100 text-emerald-800' },
  failure: { label: 'Échec', color: 'bg-red-100 text-red-800' },
  requires_2fa: { label: '2FA requis', color: 'bg-blue-100 text-blue-800' },
  '2fa_success': { label: '2FA réussi', color: 'bg-emerald-100 text-emerald-800' },
  '2fa_failure': { label: '2FA échoué', color: 'bg-red-100 text-red-800' },
};

export function LoginLogsTab({
  loginLogs, loginStats, loginLogsTotal, loginLogsPage, setLoginLogsPage,
  loginLogsFilters, setLoginLogsFilters,
  loadingLogs, loadLoginLogs, loadLoginStats,
  onExportPdf, exportingPdf,
  onDeleteLog,
}) {
  return (
    <div className="space-y-6">
      {loginStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total connexions (30j)', value: loginStats.total_logins, border: 'border-slate-200', bg: '', text: 'text-slate-900' },
            { label: 'Réussies', value: loginStats.successful, border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700' },
            { label: 'Échouées', value: loginStats.failed, border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-700' },
            { label: 'Avec 2FA', value: loginStats.requires_2fa, border: 'border-blue-200', bg: 'bg-blue-50', text: 'text-blue-700' },
          ].map(stat => (
            <Card key={stat.label} className={`${stat.border} ${stat.bg}`}>
              <CardContent className="pt-4">
                <div className={`text-2xl font-bold ${stat.text}`}>{stat.value}</div>
                <p className={`text-sm ${stat.text} opacity-75`}>{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>Journal des connexions</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { loadLoginLogs(); loadLoginStats(); }} disabled={loadingLogs} data-testid="refresh-logs-button">
                <RefreshCw className={`mr-2 h-4 w-4 ${loadingLogs ? 'animate-spin' : ''}`} /> Actualiser
              </Button>
              <Button onClick={onExportPdf} disabled={exportingPdf} className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="export-logs-pdf-button">
                {exportingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Exporter PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="space-y-1">
              <Label className="text-xs">Date début</Label>
              <Input type="date" value={loginLogsFilters.start_date} onChange={(e) => setLoginLogsFilters({ ...loginLogsFilters, start_date: e.target.value })} data-testid="filter-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date fin</Label>
              <Input type="date" value={loginLogsFilters.end_date} onChange={(e) => setLoginLogsFilters({ ...loginLogsFilters, end_date: e.target.value })} data-testid="filter-end-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input type="text" placeholder="Rechercher..." value={loginLogsFilters.email} onChange={(e) => setLoginLogsFilters({ ...loginLogsFilters, email: e.target.value })} data-testid="filter-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Statut</Label>
              <Select value={loginLogsFilters.status} onValueChange={(v) => setLoginLogsFilters({ ...loginLogsFilters, status: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="success">Succès</SelectItem>
                  <SelectItem value="failure">Échec</SelectItem>
                  <SelectItem value="requires_2fa">2FA requis</SelectItem>
                  <SelectItem value="2fa_success">2FA réussi</SelectItem>
                  <SelectItem value="2fa_failure">2FA échoué</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loadingLogs ? (
            <div className="flex justify-center py-8"><div className="spinner" style={{ width: 40, height: 40 }} /></div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date/Heure</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Adresse IP</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginLogs.map((log) => (
                    <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                      <TableCell className="text-sm">{log.timestamp ? format(parseISO(log.timestamp), "dd/MM/yyyy HH:mm", { locale: fr }) : '-'}</TableCell>
                      <TableCell className="font-medium">{log.email}</TableCell>
                      <TableCell>
                        <Badge className={statusConfig[log.status]?.color || 'bg-slate-100 text-slate-800'}>
                          {statusConfig[log.status]?.label || log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{log.ip_address}</TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{log.reason}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDeleteLog(log.id)} data-testid={`delete-log-${log.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-500">{loginLogsTotal} entrées au total</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={loginLogsPage === 1} onClick={() => setLoginLogsPage(p => p - 1)}>Précédent</Button>
                  <span className="px-3 py-1 text-sm">Page {loginLogsPage}</span>
                  <Button variant="outline" size="sm" disabled={loginLogs.length < 20} onClick={() => setLoginLogsPage(p => p + 1)}>Suivant</Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
